/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
const $ = window.jQuery;

class GravityFormsAjax {
  constructor() {
    this.selectors = {
      form: '.js-gform-ajax form',
      wrapper: '.js-gform-ajax',
    };
    this.nativeAjaxWaitMs = 1500;
    this.nativeAjaxPollMs = 50;

    window.chiselGravityFormsAjax = this;

    this.observeConfirmationWrappers();

    if (!document.querySelector(this.selectors.wrapper)) {
      return;
    }

    this.promoteFormsToAjax();
  }

  observeConfirmationWrappers() {
    const seen = new Set();

    const check = root => {
      const nodes = (root instanceof Element ? [root] : [])
        .concat(
          root && root.querySelectorAll
            ? Array.from(
                root.querySelectorAll('[id^="gform_confirmation_wrapper_"]'),
              )
            : [],
        )
        .filter(
          el => el && el.id && el.id.startsWith('gform_confirmation_wrapper_'),
        );

      nodes.forEach(el => {
        if (seen.has(el)) return;
        seen.add(el);

        const formId = el.id.replace('gform_confirmation_wrapper_', '');
        if (!formId) return;

        $(document).trigger('gform_confirmation_loaded', [formId]);
      });
    };

    check(document.body);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType === 1) check(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  getForm(formOrId) {
    if (!formOrId) {
      return null;
    }

    if (formOrId instanceof HTMLFormElement) {
      return formOrId;
    }

    if (typeof formOrId === 'string' || typeof formOrId === 'number') {
      return document.getElementById(`gform_${formOrId}`);
    }

    return null;
  }

  getFormId(formOrId) {
    const form = this.getForm(formOrId);

    if (!form) {
      return '';
    }

    return (
      form.dataset.formid ||
      form.id?.replace('gform_', '') ||
      form.querySelector('input[name="gform_submit"]')?.value?.trim() ||
      ''
    );
  }

  getWrapper(formOrId) {
    const formId = this.getFormId(formOrId);

    if (!formId) {
      return null;
    }

    return document.getElementById(`gform_wrapper_${formId}`);
  }

  getSubmissionMethodInput(formOrId) {
    const form = this.getForm(formOrId);

    if (!form) {
      return null;
    }

    return form.querySelector('[name="gform_submission_method"]');
  }

  hasNativeAjax() {
    return Boolean(window.gform?.submission?.ajax?.submitFormAjax);
  }

  canUseNativeAjax(formOrId) {
    const form = this.getForm(formOrId);

    return Boolean(form && this.getFormId(form) && this.hasNativeAjax());
  }

  waitForNativeAjax() {
    if (this.hasNativeAjax()) {
      return Promise.resolve(true);
    }

    const deadline = Date.now() + this.nativeAjaxWaitMs;

    return new Promise(resolve => {
      const poll = () => {
        if (this.hasNativeAjax()) {
          resolve(true);
          return;
        }

        if (Date.now() >= deadline) {
          resolve(false);
          return;
        }

        window.setTimeout(poll, this.nativeAjaxPollMs);
      };

      poll();
    });
  }

  ensureAjaxSubmissionMethod(formOrId) {
    const input = this.getSubmissionMethodInput(formOrId);

    if (input) {
      input.value = 'ajax';
    }
  }

  promoteFormsToAjax(context = document) {
    const forms =
      context instanceof HTMLFormElement
        ? [context]
        : Array.from(context.querySelectorAll(this.selectors.form));

    forms.forEach(form => this.ensureAjaxSubmissionMethod(form));
  }

  getAjaxConfig() {
    return (
      window.gform_theme_config?.common?.form?.ajax ||
      window.chiselScripts?.gravityForms ||
      null
    );
  }

  getAjaxUrl() {
    const config = this.getAjaxConfig();

    return (
      config?.ajaxurl ||
      config?.ajaxUrl ||
      window.chiselScripts?.configurator?.ajaxUrl ||
      ''
    );
  }

  getAjaxSubmissionNonce() {
    const config = this.getAjaxConfig();

    return (
      config?.ajax_submission_nonce || // eslint-disable-line camelcase
      config?.ajaxSubmissionNonce ||
      window.chiselScripts?.configurator?.gravityFormsAjaxSubmissionNonce ||
      ''
    );
  }

  getRequestUrl(ajaxUrl) {
    if (!ajaxUrl) {
      return '';
    }

    try {
      const url = new URL(ajaxUrl, window.location.origin);

      return `${url.pathname}${url.search}`;
    } catch (error) {
      return ajaxUrl;
    }
  }

  getDisplayFlags(formId) {
    const heading = document.querySelector(
      `#gform_wrapper_${formId} .gform_heading`,
    );

    return {
      displayTitle: Boolean(heading && heading.querySelector('.gform_title')),
      displayDescription: Boolean(
        heading && heading.querySelector('.gform_description'),
      ),
    };
  }

  parseResponse(responseText) {
    if (!responseText) {
      return null;
    }

    const match = responseText.match(
      /<!-- gf:json_start -->([\s\S]*?)<!-- gf:json_end -->/,
    );
    const payload = match ? match[1] : responseText;

    try {
      return JSON.parse(payload.trim());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to parse Gravity Forms AJAX response.', error);
      return null;
    }
  }

  captureWrapperState(wrapper) {
    if (!wrapper) {
      return {
        className: '',
        theme: '',
      };
    }

    return {
      className: wrapper.getAttribute('class') || '',
      theme: wrapper.getAttribute('data-form-theme') || '',
    };
  }

  restoreWrapperState(wrapper, state) {
    if (!wrapper || !state) {
      return;
    }

    if (state.className) {
      wrapper.setAttribute('class', state.className);
    }

    if (state.theme) {
      wrapper.setAttribute('data-form-theme', state.theme);
    }
  }

  syncPageState(form, pageNumber) {
    const formId = this.getFormId(form);
    const pages = Array.from(form.querySelectorAll('.gform_page'));

    if (!formId || !pages.length) {
      return;
    }

    pages.forEach((page, index) => {
      page.style.display = index + 1 === pageNumber ? 'block' : 'none';
    });

    const sourcePageInput = form.querySelector(
      `#gform_source_page_number_${formId}`,
    );
    const targetPageInput = form.querySelector(
      `#gform_target_page_number_${formId}`,
    );
    const nextPage = pageNumber >= pages.length ? 0 : pageNumber + 1;

    if (sourcePageInput) {
      sourcePageInput.value = String(pageNumber);
    }

    if (targetPageInput) {
      targetPageInput.value = String(nextPage);
    }

    $(document).trigger('gform_page_loaded', [formId, pageNumber]);
  }

  afterMarkupUpdate(formId, currentPage = 1) {
    const updatedForm = this.getForm(formId);

    if (updatedForm) {
      this.promoteFormsToAjax(updatedForm);
    }

    if (window.gformInitSpinner) {
      window.gformInitSpinner(formId);
    }

    this.resetSubmission(formId);
    this.triggerPostRender(formId, currentPage);
  }

  applyFallbackResponse(form, response, { displayConfirmation = true } = {}) {
    const formId = this.getFormId(form);
    const responseData = response?.data || {};

    if (!formId) {
      return;
    }

    if (responseData.page_markup) {
      const page = form.querySelector(
        `#gform_page_${formId}_${responseData.page_number}`,
      );

      if (page) {
        page.outerHTML = responseData.page_markup;
      }

      const updatedForm = this.getForm(formId);

      if (updatedForm && responseData.uploaded_files) {
        const uploadedFilesInput = updatedForm.querySelector(
          `#gform_uploaded_files_${formId}`,
        );

        if (uploadedFilesInput) {
          uploadedFilesInput.value = JSON.stringify(
            responseData.uploaded_files,
          );
        }

        updatedForm.querySelectorAll('input[type="file"]').forEach(input => {
          input.value = '';
        });
      }

      if (
        updatedForm &&
        responseData.page_number > 0 &&
        responseData.page_number !== responseData.source_page_number
      ) {
        this.syncPageState(updatedForm, responseData.page_number);
      }

      this.afterMarkupUpdate(
        formId,
        responseData.page_number || responseData.source_page_number || 1,
      );
      return;
    }

    if (responseData.form_markup) {
      const wrapper = this.getWrapper(formId);
      const wrapperState = this.captureWrapperState(wrapper);

      if (wrapper) {
        wrapper.outerHTML = responseData.form_markup;
        this.restoreWrapperState(this.getWrapper(formId), wrapperState);
      }

      this.afterMarkupUpdate(
        formId,
        responseData.page_number || responseData.source_page_number || 1,
      );
      return;
    }

    if (
      displayConfirmation &&
      (responseData.confirmation_redirect || responseData.confirmation_markup)
    ) {
      if (
        responseData.confirmation_type === 'redirect' &&
        responseData.confirmation_redirect
      ) {
        window.location = responseData.confirmation_redirect;
        return;
      }

      const wrapper = this.getWrapper(formId);
      const wrapperState = this.captureWrapperState(wrapper);

      if (wrapper && responseData.confirmation_markup) {
        wrapper.outerHTML = responseData.confirmation_markup;
        this.restoreWrapperState(this.getWrapper(formId), wrapperState);
      }

      this.resetSubmission(formId);
      return;
    }

    this.resetSubmission(formId);
  }

  submitViaFallback(form, { displayConfirmation = true } = {}) {
    const formId = this.getFormId(form);
    const ajaxUrl = this.getAjaxUrl();
    const ajaxSubmissionNonce = this.getAjaxSubmissionNonce();

    if (!form || !formId || !ajaxUrl || !ajaxSubmissionNonce) {
      return Promise.resolve(null);
    }

    this.ensureAjaxSubmissionMethod(form);

    const formData = new FormData(form);
    const { displayTitle, displayDescription } = this.getDisplayFlags(formId);

    formData.set('gform_ajax_nonce', ajaxSubmissionNonce);
    formData.set('action', 'gform_submit_form');
    formData.set('form_id', String(formId));
    formData.set('current_page_url', encodeURIComponent(window.location.href));
    formData.set('ajax_referer', encodeURIComponent(document.referrer || ''));
    formData.set('display_title', displayTitle ? '1' : '0');
    formData.set('display_description', displayDescription ? '1' : '0');

    return window
      .fetch(this.getRequestUrl(ajaxUrl), {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      })
      .then(response => response.text())
      .then(responseText => {
        const result = this.parseResponse(responseText);

        if (!result) {
          throw new Error('Unable to parse Gravity Forms AJAX response.');
        }

        if (result.success) {
          this.applyFallbackResponse(form, result, {
            displayConfirmation,
          });
          return result;
        }

        this.resetSubmission(formId);
        return result;
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Gravity Forms AJAX fallback submission failed.', error);
        this.resetSubmission(formId);
        return null;
      });
  }

  submit(formOrId, { displayConfirmation = true } = {}) {
    const form = this.getForm(formOrId);

    if (!form) {
      return Promise.resolve(null);
    }

    this.ensureAjaxSubmissionMethod(form);

    return this.waitForNativeAjax().then(hasNativeAjax => {
      if (hasNativeAjax && this.canUseNativeAjax(form)) {
        return window.gform.submission.ajax
          .submitFormAjax(this.getFormId(form), displayConfirmation)
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('Gravity Forms AJAX submission failed.', error);
            return null;
          });
      }

      return this.submitViaFallback(form, {
        displayConfirmation,
      });
    });
  }

  handleButtonClick(button) {
    const form = button?.closest('form');

    if (!form) {
      return Promise.resolve(null);
    }

    this.ensureAjaxSubmissionMethod(form);

    return this.waitForNativeAjax().then(hasNativeAjax => {
      if (hasNativeAjax && window.gform?.submission?.handleButtonClick) {
        return window.gform.submission.handleButtonClick(button);
      }

      return this.submitViaFallback(form);
    });
  }

  goToPage(formOrId, targetPage, sourcePage = null) {
    const form = this.getForm(formOrId);
    const formId = this.getFormId(form);

    if (!form || !formId) {
      return Promise.resolve(null);
    }

    const targetPageInput = form.querySelector(
      `#gform_target_page_number_${formId}`,
    );
    const sourcePageInput = form.querySelector(
      `#gform_source_page_number_${formId}`,
    );

    if (targetPageInput && targetPage !== null && targetPage !== undefined) {
      targetPageInput.value = String(targetPage);
    }

    if (sourcePageInput) {
      const resolvedSourcePage =
        sourcePage !== null && sourcePage !== undefined
          ? sourcePage
          : sourcePageInput.value;

      sourcePageInput.value = String(resolvedSourcePage);
    }

    return this.submit(form);
  }

  resetSubmission(formOrId) {
    const formId = this.getFormId(formOrId);

    if (!formId) {
      return;
    }

    if (window.gform?.submission?.ajax?.resetSubmission) {
      window.gform.submission.ajax.resetSubmission(formId);
      return;
    }

    window[`gf_submitting_${formId}`] = false;
  }

  triggerPostRender(formId, currentPage = 1) {
    if (!formId) {
      return;
    }

    if (window.gform?.core?.triggerPostRenderEvents) {
      window.gform.core.triggerPostRenderEvents(formId, currentPage);
      return;
    }

    $(document).trigger('gform_post_render', [formId, currentPage]);
  }
}

export default new GravityFormsAjax();



// WEBPACK FOOTER //
// ./wp/wp-content/themes/yanmar-chisel/src/scripts/modules/gravity-forms-ajax.js